<template>
  <v-card>
    <v-card-title></v-card-title>
    <v-card-text>
      <v-form>
        <v-container>
          <v-row dense>
            <v-col cols="8">
              <v-text-field v-model="mall.name" :label="$t('Malls.Form.name')" :error-messages="nameErrors"></v-text-field>
            </v-col>
            <v-col cols="4">
              <ColorSelector v-model="mall.color" :label="$t('Malls.Form.color')"/>
            </v-col>
            <v-col cols="12">
              <v-img class="mb-4" v-if="logoPreview" :src="logoPreview" height="100px" aspect-ratio="16/9" contain></v-img>
              <v-file-input
                placeholder="Choisissez un logo"
                prepend-icon="mdi-camera"
                label="Logo"
                v-model="logoFile"
                ref="file"
                accept="image/*"
                outlined>
              </v-file-input>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12">
              <h3 class="text-subtitle-1">{{$t('Malls.Form.informations')}}</h3>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="mall.street" :label="$t('Malls.Form.street')" prepend-inner-icon="mdi-map-marker"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="mall.postalCode" :label="$t('Malls.Form.postalCode')" prepend-inner-icon="mdi-map-marker"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="mall.city" :label="$t('Malls.Form.city')" prepend-inner-icon="mdi-map-marker"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="mall.country" :label="$t('Malls.Form.country')" prepend-inner-icon="mdi-map-marker"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="mall.phone" :label="$t('Malls.Form.phone')" prepend-inner-icon="mdi-phone"></v-text-field>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12">
              <h3 class="text-subtitle-1">
                {{$t('Malls.Form.dataValidation')}}
              </h3>
            </v-col>
            <v-col>
              <v-select :items="days" v-model="mall.validationDay" :label="$t('Malls.Form.validationDay')"></v-select>
            </v-col>
            <v-col>
              <v-text-field v-model.number="mall.validationHour" :label="$t('Malls.Form.validationHour')" prepend-inner-icon="mdi-alarm" type="number" :hint="$t('Malls.Form.validationHourHint')" :error-messages="validationHourErrors"></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="mall.validationEmail" :label="$t('Malls.Form.mail')" prepend-inner-icon="mail" :error-messages="validationEmailErrors"></v-text-field>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12">
              <h3 class="text-subtitle-1">
                {{$t('Malls.Form.others')}}
              </h3>
            </v-col>
             <v-col cols="6" xs="12">
                <DateSelector v-model="mall.openingDate" :label="$t('Malls.Form.openingDate')" :error-messages="openingDateErrors"/>
              </v-col>
              <v-col cols="6" xs="12">
                <DateSelector v-model="mall.closingDate" :label="$t('Malls.Form.closingDate')"/>
              </v-col>
            <v-col>
              <v-checkbox v-model="mall.hasCounter" :label="$t('Malls.Form.hasCounter')"></v-checkbox>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn color="grey" text @click="cancel">{{$t('Global.Action.cancel')}}</v-btn>
      <v-btn color="primary" text @click="save">{{$t('Global.Action.save')}}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { required, between, email } from 'vuelidate/lib/validators'
import { days } from '@/config/fixedValues'
import ColorSelector from '@/components/Utils/ColorSelector'
import DateSelector from '@/components/Utils/DateSelector'
import FilesPathHelpersMixin from '@/mixins/FilesPathHelpersMixin'
import theme from '@/config/theme'
export default {
  name: 'MallEditor',
  components: { ColorSelector, DateSelector },
  mixins: [FilesPathHelpersMixin],
  props: {
    defaultMall: {type: Object, required: false},
    defaultFormTitle: {type: String, required: false}
  },
  data: () => ({
    mall: null,
    default: {
      name: '',
      color: theme.primary,
      street: '',
      postalCode: '',
      city: '',
      country: '',
      phone: '',
      openingDate: null,
      closingDate: null,
      validationHour: 17,
      validationDay: 0,
      validationEmail: '',
      hasCounter: false,
      logo: ""
    },
    logoFile: null,
    days
  }),
  computed: {
    nameErrors () {
      const errors = []
      if (!this.$v.mall.name.$dirty) return errors
      !this.$v.mall.name.required && errors.push(this.$t('Global.Common.required'))
      return errors
    },
    validationHourErrors () {
      const errors = []
      if (!this.$v.mall.validationHour.$dirty) return errors
      !this.$v.mall.validationHour.required && errors.push(this.$t('Global.Common.required'))
      !this.$v.mall.validationHour.between && errors.push(this.$t('Malls.Form.validationHourInvalid'))
      return errors
    },
    validationEmailErrors () {
      const errors = []
      if (!this.$v.mall.validationEmail.$dirty) return errors
      !this.$v.mall.validationEmail.email && errors.push(this.$t('Users.Form.emailInvalid'))
      !this.$v.mall.validationEmail.required && errors.push(this.$t('Global.Common.required'))
      return errors
    },
    openingDateErrors () {
      const errors = []
      if (!this.$v.mall.openingDate.$dirty) return errors
      !this.$v.mall.openingDate.required && errors.push(this.$t('Global.Common.required'))
      return errors
    },
    logoPreview () {
      if (this.logoFile) {
        return URL.createObjectURL(this.logoFile)
      } else if (this.mall.logo) {
        return this.getMallLogoPath(this.mall.logo)
      } else {
        return '/default_logo.svg'
      }
    },
  },
  watch: {
    defaultMall () {
      this.resetForm()
    }
  },
  validations: {
    mall: {
      name: { required },
      validationHour: { required, between: between(0, 23) },
      validationEmail: { required, email },
      openingDate: { required }
    }
  },
  methods: {
    init () {
      this.mall = this.defaultMall ? {...this.defaultMall} : {...this.default}
    },
    save () {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        this.$emit('save', {...this.mall, file: this.logoFile})
      } else {
        this.$emit('invalid-form')
      }
    },
    cancel () {
      this.$emit('cancel')
      this.resetForm()
    },
    resetForm () {
      this.init()
      this.$v.$reset()
    }
  },
  async created () {
    this.init()
  }
}
</script>
